/**
 * AvatarUploader — drop / paste / click to pick a photo, resized in-browser
 * and stored as a base64 data URL. Same pattern is used for client stories,
 * client profile photos, and (eventually) anywhere else a small avatar is
 * needed. We keep the image inside the parent MongoDB document rather than
 * on disk because Railway's filesystem is ephemeral.
 *
 * Props:
 *   value         current data URL or remote URL (or empty string)
 *   onChange      called with the new data URL, or "" when removed
 *   fallbackName  used in the empty-state helper text (e.g. "Jordan's avatar")
 *   testIdPrefix  optional, defaults to "avatar" — used for data-testids
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon } from "lucide-react";
import { Button } from "./ui/button";

const AVATAR_PX = 256;
const AVATAR_QUALITY = 0.82;
const AVATAR_MAX_BYTES = 500 * 1024;  // keep in sync with backend cap

async function resizeImageToDataUrl(file) {
  if (!file) throw new Error("No file");
  if (!file.type.startsWith("image/")) throw new Error("That doesn't look like an image.");
  if (file.size > 10 * 1024 * 1024) throw new Error("Image is too large. Please pick something under 10MB.");

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("Could not read that image."));
      i.src = objectUrl;
    });
    // Center-crop to a square, then downscale.
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = (img.naturalWidth - side) / 2;
    const sy = (img.naturalHeight - side) / 2;

    const canvas = document.createElement("canvas");
    canvas.width = AVATAR_PX;
    canvas.height = AVATAR_PX;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, sx, sy, side, side, 0, 0, AVATAR_PX, AVATAR_PX);

    const dataUrl = canvas.toDataURL("image/jpeg", AVATAR_QUALITY);
    const b64 = dataUrl.split(",", 2)[1] || "";
    if ((b64.length * 3) / 4 > AVATAR_MAX_BYTES) {
      throw new Error("Image is too large after compression. Try a smaller photo.");
    }
    return dataUrl;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export default function AvatarUploader({ value, onChange, fallbackName, testIdPrefix = "avatar" }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file);
      onChange(dataUrl);
    } catch (e) {
      toast.error(e.message || "Could not process that image.");
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files && e.target.files[0];
    handleFile(f);
    e.target.value = "";
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    handleFile(f);
  };
  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type.startsWith("image/")) {
        handleFile(it.getAsFile());
        e.preventDefault();
        break;
      }
    }
  };

  return (
    <div className="mt-1.5" data-testid={`${testIdPrefix}-uploader`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={onPick}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
      {value ? (
        <div className="flex items-center gap-3">
          <img src={value} alt="" className="w-16 h-16 rounded-full object-cover border border-border" />
          <div className="flex-1 flex flex-col gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} className="rounded-none text-xs h-8" data-testid={`${testIdPrefix}-replace`}>
              Replace
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} disabled={busy} className="rounded-none text-xs h-8 text-muted-foreground hover:text-red-500" data-testid={`${testIdPrefix}-remove`}>
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div
          tabIndex={0}
          role="button"
          aria-label="Upload photo — drag and drop or click"
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onPaste={onPaste}
          className={`flex items-center gap-3 p-3 border border-dashed cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007AFF] ${dragging ? "border-[#007AFF] bg-[#007AFF]/5" : "border-border hover:border-[#007AFF]/60"}`}
          data-testid={`${testIdPrefix}-dropzone`}
        >
          <div className="w-12 h-12 rounded-full bg-[#007AFF]/10 border border-[#007AFF]/30 flex items-center justify-center flex-shrink-0">
            <ImageIcon className="w-5 h-5 text-[#007AFF]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">
              {busy ? "Processing…" : "Drop a photo here"} <span className="text-muted-foreground font-normal">or click to choose</span>
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {fallbackName ? `Used as ${fallbackName}'s avatar.` : "Resized and saved automatically. PNG, JPG, WEBP, GIF."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
